import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "../../../hooks/useScrollAnimation";
import { projects } from "../../../mocks/homeData";

export default function Projects() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollTo = (direction: "left" | "right") => {
    if (!scrollContainerRef.current) return;
    const scrollAmount = scrollContainerRef.current.clientWidth * 0.75;
    const newScrollLeft =
      direction === "left"
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;

    scrollContainerRef.current.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });

    if (direction === "left" && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    } else if (direction === "right" && activeIndex < projects.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  return (
    <section id="projects" className="bg-navy py-16 md:py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8" ref={ref}>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 md:mb-14 gap-4">
          <div>
            <div
              className={`inline-block w-12 h-1 rounded-full bg-copper mb-4 ${
                isVisible ? "animate-fade-up" : "opacity-0"
              }`}
            />
            <h2
              className={`font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-white ${
                isVisible ? "animate-fade-up" : "opacity-0"
              }`}
              style={{ animationDelay: "0.1s" }}
            >
              Tamamlanan Projelerimiz
            </h2>
          </div>
          <Link
            to="/projeler"
            className={`self-start sm:self-auto border border-white/30 hover:border-copper text-white hover:text-copper font-body text-sm font-medium px-6 py-3 rounded-md transition-all duration-300 whitespace-nowrap ${
              isVisible ? "animate-fade-up" : "opacity-0"
            }`}
            style={{ animationDelay: "0.2s" }}
          >
            Tüm Projeler
          </Link>
        </div>

        <div className="relative">
          <div
            ref={scrollContainerRef}
            className="flex gap-5 md:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {projects.map((project, index) => (
              <div
                key={project.id}
                className={`flex-shrink-0 w-[300px] sm:w-[380px] md:w-[440px] snap-start group rounded-lg overflow-hidden cursor-pointer ${
                  isVisible ? "animate-fade-up" : "opacity-0"
                }`}
                style={{ animationDelay: `${0.15 + index * 0.1}s` }}
              >
                <div className="relative h-64 sm:h-72 md:h-80 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                    <h3 className="font-heading font-bold text-lg md:text-xl text-white mb-1">
                      {project.name}
                    </h3>
                    <p className="font-body text-sm text-white/70">
                      {project.location} &middot; {project.year}
                    </p>
                    <p className="font-body text-xs text-white/60 mt-1">
                      {project.reference}
                    </p>
                  </div>
                  <div className="absolute top-4 right-4 bg-copper/90 text-white font-body text-xs font-medium px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="flex items-center gap-1">
                      <i className="ri-arrow-right-line" />
                      İncele
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 mt-6 md:mt-8">
            <button
              onClick={() => scrollTo("left")}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full border border-white/30 hover:border-copper flex items-center justify-center text-white hover:text-copper transition-all duration-300"
            >
              <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                <i className="ri-arrow-left-s-line text-xl" />
              </div>
            </button>
            <div className="flex gap-2">
              {projects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setActiveIndex(index);
                    if (scrollContainerRef.current) {
                      const card = scrollContainerRef.current.children[index] as HTMLElement;
                      card?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
                    }
                  }}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    index === activeIndex ? "bg-copper w-8" : "bg-white/30 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={() => scrollTo("right")}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full border border-white/30 hover:border-copper flex items-center justify-center text-white hover:text-copper transition-all duration-300"
            >
              <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xl" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}